/**
 * Step 5a: ask for a reset link.
 *
 * The confirmation screen is shown unconditionally, before the response is
 * even inspected for whether an account exists — the acceptance criteria
 * require the same response either way, and the easiest way to be sure of
 * that is to have no branch that could differ.
 */

import { useState, type FormEvent } from 'react'
import { Link } from 'react-router'
import { requestPasswordResetInputSchema } from '../../shared/auth-schemas.ts'
import type { MessageKey } from '../../shared/message-keys.ts'
import { Alert } from '../components/Alert.tsx'
import { Field } from '../components/Field.tsx'
import { Turnstile } from '../components/Turnstile.tsx'
import { useI18n } from '../i18n/index.tsx'
import { messageKeyFor } from '../lib/api.ts'
import { authClient } from '../lib/auth-client.ts'
import { useClientConfig } from '../lib/use-client-config.ts'

export function ForgotPassword() {
  const { t, locale } = useI18n()
  const config = useClientConfig()

  const [email, setEmail] = useState('')
  const [turnstileToken, setTurnstileToken] = useState('')
  const [turnstileResets, setTurnstileResets] = useState(0)
  const [error, setError] = useState<MessageKey | null>(null)
  const [busy, setBusy] = useState(false)
  const [sent, setSent] = useState(false)

  async function onSubmit(event: FormEvent) {
    event.preventDefault()
    setError(null)

    const parsed = requestPasswordResetInputSchema.safeParse({ email, turnstileToken })
    if (!parsed.success) {
      setError((parsed.error.issues[0]?.message ?? 'error.bad_request') as MessageKey)
      return
    }

    setBusy(true)
    const { error: failure } = await authClient.requestPasswordReset({
      email: parsed.data.email,
      redirectTo: `${window.location.origin}/reset-password`,
      fetchOptions: {
        headers: { 'x-turnstile-token': parsed.data.turnstileToken },
      },
    })
    setBusy(false)

    // Only the two failures that are about the caller rather than the
    // account are surfaced. "No such account" is not one of them.
    const key = failure ? messageKeyFor(failure) : null
    if (key === 'error.rate_limited' || key === 'error.turnstile_failed' || key === 'error.turnstile_missing') {
      setTurnstileToken('')
      setTurnstileResets((n) => n + 1)
      setError(key)
      return
    }

    setSent(true)
  }

  if (sent) {
    return (
      <section className="panel">
        <h1>{t('forgot.sent.title')}</h1>
        <p>{t('forgot.sent.body')}</p>
        {/* The same screen either way (issue #40): a way out for a reader who
            has no account, without saying whether they have one. */}
        <p className="panel__links">
          <Link to="/signin">{t('signin.title')}</Link>
          <Link to="/register">{t('signin.no_account')}</Link>
        </p>
      </section>
    )
  }

  return (
    <section className="panel">
      <h1>{t('forgot.title')}</h1>
      <p>{t('forgot.lead')}</p>

      <form onSubmit={(event) => void onSubmit(event)} noValidate>
        <Field
          label={t('signin.email.label')}
          type="email"
          name="email"
          autoComplete="email"
          required
          value={email}
          onChange={(event) => {
            setEmail(event.target.value)
          }}
        />

        {config ? (
          <Turnstile
            siteKey={config.turnstileSiteKey}
            locale={locale}
            onToken={setTurnstileToken}
            resetSignal={turnstileResets}
          />
        ) : null}

        {error ? <Alert tone="error">{t(error)}</Alert> : null}

        <button type="submit" className="button" disabled={busy}>
          {t('forgot.submit')}
        </button>
      </form>
    </section>
  )
}
